import { useState } from "react"
import Checkbox from "@/v2-ui/checkbox"
import Icon from "@/v2-ui/icon"
import Label from "@/v2-ui/label"
import cn from "@/v2-ui/utils/utils.cn"
import {
  isPersonLens,
  useFeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  isClosedForLens,
  objectOfLens,
  reachStateOf,
  reachesThroughLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import LicenseFeatureGroupContentItem from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupContentItem"
import LicenseFeatureGroupDetailsHoverCard from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupDetailsHoverCard"
import LicenseFeatureGroupIncludedMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupIncludedMark"
import LicenseFeatureGroupReachIndicator from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachIndicator"
import { ROW_NOTE_CLASS } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import {
  pluralize,
  type FeatureGroupPickedLike,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  grantOf,
  hasCategoryRule,
  impactOf,
  matchesContentFilter,
  rulesWithCategoryRule,
  toPickedContent,
  type LicenseFeatureGroupContentGroup as ContentGroup,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.content.utils"

export type LicenseFeatureGroupContentGroupProps = {
  group: ContentGroup
  noun: string
  categoryLabelOf(code: string): string
  /** The operator's filter; a group opens on its matches and hides when it has none. */
  filter: string
  /** The class's "everything" rule is on: every box here is ticked by it. */
  isAllGranted: boolean
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  featureGroupName: string
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onRulesChange(rules: readonly FeatureGroupRuleLike[]): void
  onSetPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ): void
}

// Where a user meets the items: a layer category, or the countries a base
// map is offered in. A collapsed row with a tri-state checkbox and the
// counts that matter — how many, how many package-only, what is pending —
// that opens to its items. For a layer category the checkbox IS the
// category rule (granted now and for every layer filed there later), and
// the header says so; for a grouping with no rule behind it, ticking the
// header ticks every item by hand, and new layers will not follow. Through
// the customer lens the count is what reaches the customer's users, and a
// group they already have in full (open class, nothing added here) shows
// the fixed tick: nothing to decide.
function LicenseFeatureGroupContentGroup(
  props: LicenseFeatureGroupContentGroupProps,
) {
  const {
    group,
    noun,
    categoryLabelOf,
    filter,
    isAllGranted,
    rules,
    persistedRules,
    featureGroupName,
    isPicked,
    wasPicked,
    onRulesChange,
    onSetPicked,
  } = props

  const [isOpenByHand, setIsOpenByHand] = useState(false)
  const lens = useFeatureGroupLens()
  const isClassClosed =
    group.items.length > 0 &&
    isClosedForLens(lens, group.items[0].FeatureTypeCode)
  const isFiltering = filter.trim() !== ""
  const visibleItems = isFiltering
    ? group.items.filter((item) =>
        matchesContentFilter(item, filter, group.label),
      )
    : group.items
  const isOpen = isFiltering || isOpenByHand

  if (visibleItems.length === 0) return null

  const nameCounts = new Map<string, number>()
  for (const item of group.items) {
    nameCounts.set(item.Name, (nameCounts.get(item.Name) ?? 0) + 1)
  }
  const isGranted = (item: ContentGroup["items"][number]) =>
    grantOf(item, rules, isPicked) !== null
  const wasGranted = (item: ContentGroup["items"][number]) =>
    grantOf(item, persistedRules, wasPicked) !== null
  const isReached = (item: ContentGroup["items"][number]) =>
    reachesThroughLens(lens, item, isGranted(item))
  // The checkbox follows the draft; the count follows what reaches the
  // people the lens is about (the same thing through the package lens).
  const impact = impactOf(group.items, isGranted)
  const reached = impactOf(group.items, isReached)
  const added = group.items.filter(
    (item) => isGranted(item) && !wasGranted(item),
  ).length
  const removed = group.items.filter(
    (item) => !isGranted(item) && wasGranted(item),
  ).length
  const hasRule =
    group.categoryCode !== null && hasCategoryRule(rules, group.categoryCode)
  const headerState =
    impact.granted === 0
      ? false
      : impact.granted === impact.total
        ? true
        : "indeterminate"
  const isFixed =
    lens.kind === "customer" &&
    !isClassClosed &&
    !isAllGranted &&
    impact.granted === 0 &&
    reached.granted === reached.total

  function setGroupGranted(granted: boolean) {
    if (group.categoryCode) {
      onRulesChange(rulesWithCategoryRule(rules, group.categoryCode, granted))
      // A rule makes picks inside it redundant; clearing it clears them too.
      onSetPicked(group.items.map(toPickedContent), false)
      return
    }
    onSetPicked(group.items.map(toPickedContent), granted)
  }

  function hintOf() {
    if (isAllGranted) return null
    if (hasRule) {
      return lens.kind === "customer"
        ? "added here · whole category, incl. future additions"
        : "whole category, incl. future additions"
    }
    if (impact.granted > 0) {
      return lens.kind === "customer"
        ? `${impact.granted} added here · no future additions`
        : "picked one by one · no future additions"
    }
    return null
  }
  const hint = hintOf()

  return (
    <li>
      <div className="flex items-center gap-2.5 bg-muted/20 py-1 pl-2 pr-2">
        <Label
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 font-normal",
            isFixed || isPersonLens(lens) ? "cursor-default" : "cursor-pointer",
          )}
        >
          {isPersonLens(lens) ? (
            <LicenseFeatureGroupReachIndicator
              state={reachStateOf(reached.granted, reached.total)}
              title={`${reached.granted} of ${pluralize(reached.total, noun)} in ${group.label} reach ${lens.userName}; the rows say which.`}
            />
          ) : isFixed && lens.kind === "customer" ? (
            <LicenseFeatureGroupIncludedMark
              title={`Included: every ${noun} in ${group.label} reaches ${lens.customerName}'s users already.`}
            />
          ) : (
            <Checkbox
              checked={isAllGranted ? true : headerState}
              disabled={isAllGranted}
              aria-label={
                group.categoryCode
                  ? `Grant every ${noun} in ${group.label}, now and in future releases`
                  : `Grant every ${noun} in ${group.label}`
              }
              onCheckedChange={(checked) => {
                setGroupGranted(checked === true)
              }}
            />
          )}
          <LicenseFeatureGroupDetailsHoverCard
            title={group.label}
            facts={[
              { label: "Holds", value: pluralize(impact.total, noun) },
              lens.kind === "customer"
                ? {
                    label: `Reaches ${objectOfLens(lens)}`,
                    value: `${reached.granted} of ${reached.total}`,
                  }
                : {
                    label: "Package-only",
                    value:
                      impact.locked === 0
                        ? "none — everyone gets these"
                        : `${impact.locked}, of which this package grants ${impact.grantedLocked}`,
                  },
              ...(group.categoryCode
                ? [
                    {
                      label: "Header tick",
                      value: hasRule
                        ? "the whole category as a rule: layers filed here later are included"
                        : "grants the whole category as a rule, future layers included; single ticks do not",
                    },
                  ]
                : [
                    {
                      label: "Header tick",
                      value:
                        "ticks every item here by hand; a new item does not follow",
                    },
                  ]),
            ]}
          >
            <span className="min-w-[5rem] flex-1 truncate text-xs font-medium">
              {group.label}
            </span>
          </LicenseFeatureGroupDetailsHoverCard>
          {hint && (
            <span className={ROW_NOTE_CLASS} title={hint}>
              {hint}
            </span>
          )}
          {(added > 0 || removed > 0) && (
            <span className="text-[11px] tabular-nums text-primary">
              {added > 0 && `+${added}`}
              {added > 0 && removed > 0 && " "}
              {removed > 0 && `−${removed}`}
            </span>
          )}
          <span className="text-xs tabular-nums text-muted-foreground">
            {reached.granted}/{reached.total}
            {lens.kind === "package" && impact.locked > 0 && (
              <span className="ml-1 text-[11px]">
                · {impact.locked} package-only
              </span>
            )}
          </span>
        </Label>
        <button
          type="button"
          className="ml-1 flex h-5 shrink-0 items-center justify-center rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-expanded={isOpen}
          aria-label={isOpen ? `Hide ${group.label}` : `Show ${group.label}`}
          disabled={isFiltering}
          data-feature-code="UI_BackofficeFeatureGroup_Content_GroupToggle"
          onClick={() => {
            setIsOpenByHand(!isOpenByHand)
          }}
        >
          <Icon src={isOpen ? "caret-down" : "caret-right"} size={11} />
        </button>
      </div>
      {isOpen && (
        <ul className="divide-y">
          {visibleItems.map((item) => (
            <LicenseFeatureGroupContentItem
              key={item.Code}
              item={item}
              showsCode={(nameCounts.get(item.Name) ?? 0) > 1}
              noun={noun}
              categoryLabelOf={categoryLabelOf}
              rules={rules}
              persistedRules={persistedRules}
              featureGroupName={featureGroupName}
              isPicked={isPicked}
              wasPicked={wasPicked}
              onSetPicked={onSetPicked}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

export default LicenseFeatureGroupContentGroup
